import Result, { match } from 'true-myth/result';
import debounce, { type DebouncedFunction } from 'debounce';
import { VoiceBotStatus } from './hal';
import { environment } from '../../environments/environment';
import { deepgramWordPronounceMapping, stsConfig, numberWordToRomanMap, timeUnits } from './constants';
import { MicrophoneService } from '../services/microphone.service';
import { CommonUtilitiesService } from '../services/commonUtilities.service';
import { getApiAuthHeaders } from '../services/session-auth.store';
const idleTimeoutMs = '300000'; // 5 Minutes
const DG_API_KEY = '';
const DG_URL = environment.deepgramAgentWSURL;
const apiUrl = environment.apiUrl;
const tableDetectionMessage = "The table with all the details is ready. Please check it, then come back to continue.\n\n";

const agentConfig = stsConfig;

enum AgentEvent {
  NO_KEY = 'no key',
  NO_URL = 'no url',
  NO_CONFIG = 'no config',
  EMPTY_AUDIO = 'empty audio',
  SOCKET_OPEN = 'socket open',
  SOCKET_CLOSE = 'socket close',
  CONNECTION_TIMEOUT = 'connection timeout',
  FAILED_SETUP = 'failed setup',
  FAILED_TO_CONNECT_USER_MEDIA = 'failed to connect user media',
  UNKNOWN_MESSAGE = 'unknown message',
  STRUCTURED_MESSAGE = 'structured message',
  CLIENT_MESSAGE = 'client message',
}

type AgentEventDetail = { variant: AgentEvent; detail?: object };

enum MessageType {
  /** first detection of user speech */
  UserStartedSpeaking = 'UserStartedSpeaking',
  /** EOT model decides the user's ended their turn */
  EndOfThought = 'EndOfThought',
  /** Agent audio starts coming across the socket */
  AgentStartedSpeaking = 'AgentStartedSpeaking',
  /** All agent audio is sent (different from TTS being complete!) */
  AgentAudioDone = 'AgentAudioDone',
  /** Detection of Function Call Request */
  FunctionCallRequest = 'FunctionCallRequest',
  /** Detection of the Conversation Text Coming */
  ConversationText = "ConversationText"
}

enum Sender {
  User,
  Agent,
}

const normalizeVolume = (
  analyser: AnalyserNode,
  dataArray: Uint8Array,
  normalizationFactor: number
): number => {
  analyser.getByteFrequencyData(dataArray);
  const sum = dataArray.reduce((acc, val) => acc + val, 0);
  const average = sum / dataArray.length;
  return Math.min(average / normalizationFactor, 1);
};

const convertFloat32ToInt16 = (buffer: Float32Array): ArrayBuffer => {
  const buf = new Int16Array(buffer.length);
  for (let l = 0; l < buffer.length; l += 1) {
    buf[l] = Math.min(1, buffer[l] ?? 0) * 0x7fff;
  }
  return buf.buffer;
};

const sendMicTo =
  (socket: WebSocket) =>
  (event: AudioProcessingEvent): void => {
    const inputData = event.inputBuffer.getChannelData(0);
    const audioDataToSend = convertFloat32ToInt16(inputData);
    if (socket.readyState === WebSocket.OPEN) socket.send(audioDataToSend);
  };

const createAnalyser = (context: AudioContext): AnalyserNode => {
  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.96;
  return analyser;
};

const tryDisconnect = (source?: AudioNode | null, destination?: AudioNode) => {
  if (!source || !destination) return;
  try {
    source.disconnect(destination);
  } catch {}
};

interface UserMediaNodes {
  microphone: MediaStreamAudioSourceNode;
  processor: ScriptProcessorNode;
  analyser: AnalyserNode;
}

const getNodes = async (
  context: AudioContext,
  microphoneService: MicrophoneService
): Promise<Result<UserMediaNodes, AgentEventDetail>> =>
  navigator.mediaDevices
    .getUserMedia({
      // these are constraints that browsers are free to ignore
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: false,
      },
    })
    .then((stream) => {
        microphoneService.setStream(stream);
        return Result.ok({
          microphone: context.createMediaStreamSource(stream),
          processor: context.createScriptProcessor(4096, 1, 1),
          analyser: createAnalyser(context),
        });
      })
      .catch(() =>
        Result.err({ variant: AgentEvent.FAILED_TO_CONNECT_USER_MEDIA })
      );

const getConfigString = (
  agentConfigString: string
): Result<string, AgentEventDetail> => {
  try {
    const agentConfig = JSON.parse(agentConfigString);
    return Result.ok(JSON.stringify(agentConfig));
  } catch {
    return Result.err({ variant: AgentEvent.FAILED_SETUP });
  }
};

const sendVolumeUpdates = (
  analyser: AnalyserNode,
  hal: HTMLElement,
  attributeName: string
) => {
  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  const getVolume = () => {
    if (hal.getAttribute('orb-state') === VoiceBotStatus.Active) {
      hal.setAttribute(
        attributeName,
        normalizeVolume(analyser, dataArray, 48).toString()
      );
    }
    if (analyser.context.state === 'running') requestAnimationFrame(getVolume);
  };
  getVolume();
};

const AudioContextClass =
  window.AudioContext ||
  (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

const openWebSocket = async (
  url: string,
  apiKey: string
): Promise<Result<WebSocket, AgentEventDetail>> => {
  try {
    const socket = new WebSocket(url, ['token', apiKey]);
    socket.binaryType = 'arraybuffer';

    // The following promise can resolve in one of several different ways depending on what happens
    // with the socket connection. Whichever event happens first wins; the other resolves are then
    // noöps.
    return await new Promise<Result<WebSocket, AgentEventDetail>>((resolve) => {
      socket.addEventListener('open', () => resolve(Result.ok(socket)));

      // If the server immediately terminates the connection (e.g. because the user has insufficient
      // funds), we'll receive the "close" event without first receiving an "open" event.
      socket.addEventListener('close', (event) =>
        resolve(Result.err({ variant: AgentEvent.SOCKET_CLOSE, detail: event }))
      );

      // If the socket fails to connect in a reasonable amount of time, resolve with an error;
      // the socket gets cleaned up when the calling code calls this.disconnect()
      setTimeout(
        () => resolve(Result.err({ variant: AgentEvent.CONNECTION_TIMEOUT })),
        10000
      );
    });
  } catch {
    return Result.err({ variant: AgentEvent.FAILED_SETUP });
  }
};

const pairResults = <A, B, E>(
  r1: Result<A, E>,
  r2: Result<B, E>
): Result<[A, B], E> => r1.andThen((ok1) => r2.map((ok2) => [ok1, ok2]));

enum Attributes {
  url = 'url',
  width = 'width',
  height = 'height',
}

enum ObservedAttributes {
  config = 'config',
  idleTimeoutMs = 'idle-timeout-ms',
}

customElements.define(
  'deepgram-agent',
  class AgentElement extends HTMLElement {
    private socket: WebSocket | null;

    private microphone: MediaStreamAudioSourceNode | null;
    private processor: ScriptProcessorNode | undefined;
    private scheduledPlaybackSources: Set<AudioBufferSourceNode>;
    private startTime: number;
    private ttsAnalyser: AnalyserNode | undefined;
    private ttsContext: AudioContext | undefined;
    private micAnalyser: AnalyserNode | undefined;
    private micContext: AudioContext | undefined;

    private hal: HTMLElement;

    apiKey: string | undefined;
    cif:string = "";
    currentSessionId:string = "";

    private activeSender: Sender | null;
    private lastUserQuestionSpoken: string = "";

    private startIdleTimeout: DebouncedFunction<() => void>;

    constructor(private microphoneService: MicrophoneService) {
      super();
      this.socket = null;
      this.microphone = null;

      // unlike the socket and the mic setup, we can leave the tts context
      // sitting around
      this.scheduledPlaybackSources = new Set();
      this.startTime = -1;
      this.activeSender = null;
      this.hal = document.createElement('deepgram-hal');

      try {
        if (!AudioContextClass) {
          throw new Error('Web Audio API is not supported in this browser');
        }
        this.ttsContext = new AudioContextClass({
          latencyHint: 'interactive',
          // might be nice to delegate to the machine here, but the /agent API
          // doesn't seem to tolerate a 44.1k sample rate for output
          sampleRate: 48000,
        });
        this.ttsAnalyser = createAnalyser(this.ttsContext);
        // this.ttsAnalyser.connect(this.ttsContext.destination);

        this.micContext = new AudioContextClass();

        sendVolumeUpdates(this.ttsAnalyser, this.hal, 'agent-volume');
      } catch {
        this.dispatch(AgentEvent.FAILED_SETUP);
      }

      this.startIdleTimeout = debounce(() => {}, 0);
    }

    static get observedAttributes() {
      return Object.values(ObservedAttributes);
    }

    private replaceIdleTimeout(timeoutString: string | null) {
      if (Number.isNaN(Number(idleTimeoutMs))) return;
      this.startIdleTimeout.clear();
      this.startIdleTimeout = debounce(
        () => this.disconnect('idle timeout'),
        Number(idleTimeoutMs)
      );
    }

    /*private replaceDeepgramAgentPronounceWords(sentence: string): string {
      return sentence.replace(/\b\w+\b/g, (word) => {
        return deepgramWordPronounceMapping[word] || word;
      });
    }*/

    private replaceDeepgramAgentPronounceWords(sentence: string): string {
      return sentence.replace(/\b\w+\b/g, (word) => {
        const mapped = deepgramWordPronounceMapping[word.toUpperCase()];
        return mapped || word;
      });
    }

    // Adding this method till we have other proper solution in place to replace roman project names like
    // e.g Student Housing three to Student Housing III
    convertContextualRomanNumerals(input: string): string {
      const words = input.split(' ');
      const result: string[] = [];

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const cleanWord = word.replace(/[.,!?]/g, '').toLowerCase();
        const nextWord = words[i + 1]?.replace(/[.,!?]/g, '').toLowerCase() || '';

        // Only convert to Roman if NOT followed by a time unit
        if (
          numberWordToRomanMap[cleanWord] &&
          !timeUnits.includes(nextWord)
        ) {
          result.push(numberWordToRomanMap[cleanWord]);
        } else {
          result.push(word);
        }
      }

      return result.join(' ');
    }

    private replaceMarkdownTables(input: string): string {
      const tableRegex = /(\|.*\|\n)(\|[-| ]+\|\n)((\|.*\|\n?)+)/g;
    
      return input.replace(tableRegex, tableDetectionMessage);
    }

    private dispatch(variant: AgentEvent, detail?: object) {
      this.dispatchEvent(
        detail ? new CustomEvent(variant, { detail }) : new CustomEvent(variant)
      );
    }

    sendClientMessage(message: ArrayBuffer | string): void {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(message);
        // Would consumers like us to emit something when audio is sent?
        if (typeof message === 'string') {
          this.dispatch(AgentEvent.CLIENT_MESSAGE, JSON.parse(message));
        }
      }
    }

    private connectNodes({
      microphone,
      processor,
      analyser,
    }: UserMediaNodes): Result<UserMediaNodes, AgentEventDetail> {
      this.microphone = microphone;
      this.processor = processor;
      this.micAnalyser = analyser;

      if (!this.micContext || !this.ttsAnalyser || !this.ttsContext) {
        return Result.err({ variant: AgentEvent.FAILED_SETUP });
      }
      this.microphone.connect(this.micAnalyser);
      this.microphone.connect(this.processor);
      this.processor.connect(this.micContext.destination);
      this.ttsAnalyser.connect(this.ttsContext.destination);
      sendVolumeUpdates(this.micAnalyser, this.hal, 'user-volume');
      sendVolumeUpdates(this.ttsAnalyser, this.hal, 'agent-volume');

      return Result.ok({ microphone, processor, analyser });
    }

    private disconnectNodes() {
      tryDisconnect(this.microphone, this.micAnalyser);
      tryDisconnect(this.microphone, this.processor);
      tryDisconnect(this.processor, this.micContext?.destination);
      tryDisconnect(this.ttsAnalyser, this.ttsContext?.destination);
    }

    private async suspendContext() {
      if (this.micContext) await this.micContext.suspend();
      if (this.ttsContext) await this.ttsContext.suspend();
    }

    private async resumeContext() {
      if (this.micContext) await this.micContext.resume();
      if (this.ttsContext) await this.ttsContext.resume();
    }

    private async closeContext() {
      if (this.micContext) await this.micContext.close();
      if (this.ttsContext) await this.ttsContext.close();
    }

    private playAudio(data: ArrayBuffer) {
      if (!this.ttsAnalyser) return;
      const { context } = this.ttsAnalyser;

      // Prime the audio context to help route correctly
      const silentBuffer = context.createBuffer(1, 1, 48000);
      const silentSource = context.createBufferSource();
      silentSource.buffer = silentBuffer;
      silentSource.connect(context.destination);
      silentSource.start(0);

      const audioDataView = new Int16Array(data);
      if (audioDataView.length === 0) {
        this.dispatch(AgentEvent.EMPTY_AUDIO);
        return;
      }

      const buffer = context.createBuffer(1, audioDataView.length, 48000);
      const channelData = buffer.getChannelData(0);

      // Convert linear16 PCM to float [-1, 1]
      audioDataView.forEach((value, index) => {
        channelData[index] = value / 32768;
      });

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ttsAnalyser);
      this.ttsAnalyser.connect(context.destination); // Ensure final routing

      const { currentTime } = context;
      if (this.startTime < currentTime) {
        this.startTime = currentTime;
      }

      source.addEventListener('ended', () => {
        this.scheduledPlaybackSources.delete(source);
        this.checkAndHandleTtsPlaybackCompleted();
      });
      source.start(this.startTime);
      this.startTime += buffer.duration;

      this.scheduledPlaybackSources.add(source);
    }

    private checkAndHandleTtsPlaybackCompleted() {
      if (
        this.scheduledPlaybackSources.size === 0 &&
        this.activeSender === null
      ) {
        this.startIdleTimeout();
      }
    }

    private clearActiveSenderIf(activeSender: Sender) {
      if (this.activeSender === activeSender) this.activeSender = null;
    }

    private async handleSocketMessage(message: MessageEvent) {
      if (message.data instanceof ArrayBuffer) {
        this.playAudio(message.data);
      } else {
        try {
          const data = JSON.parse(message.data);
          console.log(message.data);

          switch (data.type) {
            case MessageType.UserStartedSpeaking:
              this.activeSender = Sender.User;
              this.stopTts();
              this.startIdleTimeout.clear();
              break;
            case MessageType.EndOfThought:
              this.clearActiveSenderIf(Sender.User);
              break;
            case MessageType.AgentStartedSpeaking:
              this.activeSender = Sender.Agent;
              break;
            case MessageType.AgentAudioDone:
              this.clearActiveSenderIf(Sender.Agent);
              this.checkAndHandleTtsPlaybackCompleted();
              break;
            case MessageType.ConversationText:
              const role = data.role;
              if (this.role = "user") {
                this.lastUserQuestionSpoken = data.content;
              }

              break;
            case MessageType.FunctionCallRequest:
              const functionCallId = data.function_call_id;
              const inputQuestion = data.input.question;
              const functionName = data.function_name;

              //console.log('Function input:', inputQuestion);
              console.log("User Question ::: " + this.lastUserQuestionSpoken);

              if (functionName === 'ask_question') {
                const agentElement = document.querySelector('deepgram-agent');
                if (agentElement) {
                    // Getting cif and session id values
                    this.cif = agentElement.getAttribute('cif');
                    this.currentSessionId = agentElement.getAttribute('currentsessionid');
                }

                this.lastUserQuestionSpoken = this.convertContextualRomanNumerals(this.lastUserQuestionSpoken);

                // Make the API call manually
                const response = await fetch(`${apiUrl}/ask_question`, {
                    method: 'POST',
                    headers: getApiAuthHeaders({
                      'Content-Type': 'application/json',
                      'X-GFH-AI-CHATBOT-CIF': this.cif,
                      'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId
                    }),
                    body: JSON.stringify({
                      question: this.lastUserQuestionSpoken,
                      session_name: 'default'
                    })
                  });

                  const result = await response.json();
                  console.log('Function Call Response:', result);


                  /*const answerChunks = [
                    "Your current portfolio size is $8,682,859 USD",
                    "and has shown a 104.95% return",
                    "over the past 1 year and 9 months.",
                    "Would you like a detailed breakdown of your portfolio's performance?"
                  ];

                  let index = 0;

                  const interval = setInterval(() => {
                    if (index >= answerChunks.length) {
                      clearInterval(interval);
                      return;
                    }

                    this.sendClientMessage(
                      JSON.stringify({
                        type: 'FunctionCallResponse',
                        function_call_id: functionCallId,
                        output: answerChunks[index]
                      })
                    );

                    index++;
                  }, 1000);*/

                  const formattedOutput = this.replaceDeepgramAgentPronounceWords(result.answer);
                  // If we detect table then we need to clean that before sending to voice agent

                  const cleanedOutput = this.replaceMarkdownTables(formattedOutput);
                  console.log("Cleaned Output ::: " + cleanedOutput);

                  // Send response back to Deepgram
                  this.sendClientMessage(
                    JSON.stringify({
                      type: 'FunctionCallResponse',
                      function_call_id: functionCallId,
                      output: cleanedOutput
                    })
                  );
              }

              break;
            default:
              break;
          }

          this.dispatch(AgentEvent.STRUCTURED_MESSAGE, data);
        } catch {
          this.dispatch(AgentEvent.UNKNOWN_MESSAGE);
        }
      }
    }

    async connect(): Promise<void> {
      // Since multiple attributes/properties on the element can be changed "at once" when starting
      // the agent (with successive synchronous setAttribute() calls) and any of those changes may
      // have triggered the connect, wait until all such updates have been made before executing the
      // connect routine, so we're sure we have all the current values.
      await Promise.resolve();

      const { apiKey } = this;
      const url = DG_URL;
      if (!url) {
        this.dispatch(AgentEvent.NO_URL);
        return;
      }
      const configAttr = JSON.stringify(agentConfig);
      if (!configAttr) {
        this.dispatch(AgentEvent.NO_CONFIG);
        return;
      }

      if (!this.micContext || !this.ttsContext || !this.ttsAnalyser) {
        this.dispatch(AgentEvent.FAILED_SETUP);
        return;
      }

      await this.resumeContext();

      /*const nodesAndMicAndConfigString: Result<
        [UserMediaNodes, WebSocket, string],
        AgentEventDetail
      > = pairResults(
        (await getNodes(this.micContext, this.microphoneService)).andThen((nodes) =>
          this.connectNodes(nodes)
        ),
        await openWebSocket(url, DG_API_KEY)
      ).andThen(([nodes, socket]) =>
        getConfigString(configAttr).map((configString) => [
          nodes,
          socket,
          configString,
        ])
      );*/

      const [nodesResult, socketResult] = await Promise.all([
        getNodes(this.micContext, this.microphoneService).then((r) => r.andThen((nodes) => this.connectNodes(nodes))),
        openWebSocket(url, DG_API_KEY),
      ]);
      
      const nodesAndMicAndConfigString = pairResults(nodesResult, socketResult).andThen(([nodes, socket]) =>
        getConfigString(configAttr).map((configString) => [nodes, socket, configString])
      );
      

      match(
        {
          Ok: ([{ processor, analyser }, socket, configString]: [
            UserMediaNodes,
            WebSocket,
            string
          ]) => {
            //sendVolumeUpdates(analyser, this.hal, 'user-volume');

            const sendMicToSocket = sendMicTo(socket);

            socket.addEventListener('close', (event: CloseEvent) => {
              this.dispatch(AgentEvent.SOCKET_CLOSE, event);
              processor.removeEventListener('audioprocess', sendMicToSocket);
              this.startIdleTimeout.clear();
            });
            socket.addEventListener(
              'message',
              this.handleSocketMessage.bind(this)
            );
            this.socket = socket;

            this.dispatch(AgentEvent.SOCKET_OPEN);
            this.sendClientMessage(configString);
            this.startIdleTimeout();

            processor.addEventListener('audioprocess', sendMicToSocket);

            //this.hal.setAttribute('orb-state', VoiceBotStatus.Active);

            requestAnimationFrame(() => {
              sendVolumeUpdates(analyser, this.hal, 'user-volume');
              this.hal.setAttribute('orb-state', VoiceBotStatus.Active);
            });
          },
          Err: async ({ variant, detail }) => {
            await this.disconnect();
            this.dispatch(variant, detail);
          },
        },
        nodesAndMicAndConfigString
      );
    }

    private stopTts() {
      this.scheduledPlaybackSources.forEach((source) => source.stop());
      this.scheduledPlaybackSources.clear();
      this.startTime = -1;
    }

    private async clearMicrophone() {
      this.microphone?.mediaStream.getTracks().forEach((t) => t.stop());
      this.microphone = null;
    }

    private clearSocket(reason?: string) {
      return new Promise<void>((resolve) => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.addEventListener('close', () => {
            this.socket = null;
            resolve();
          });

          if (reason) {
            this.socket.close(1000, reason);
          } else {
            this.socket.close(1000);
          }
        } else {
          this.socket = null;
          resolve();
        }
      });
    }

    async disconnect(reason?: string): Promise<void> {
      this.stopTts();
      await this.suspendContext();
      this.disconnectNodes();
      await this.clearSocket(reason);
      await this.clearMicrophone();
      this.hal.setAttribute('orb-state', VoiceBotStatus.Sleeping);
    }

    async restart() {
      await this.disconnect();
      this.connect();
    }

    async attributeChangedCallback(
      name: string,
      oldValue: string,
      newValue: string
    ) {
      switch (name) {
        case ObservedAttributes.config:
          if (!oldValue && newValue) {
            await this.connect();
          } else if (oldValue && !newValue) {
            await this.disconnect();
          } else if (newValue && this.socket) {
            this.restart();
          }
          break;

        case ObservedAttributes.idleTimeoutMs:
          this.replaceIdleTimeout(idleTimeoutMs);
          break;

        default:
          break;
      }
    }

    async disconnectedCallback() {
      await this.disconnect();
      await this.closeContext();
    }

    async connectedCallback() {
      this.hal.setAttribute('agent-volume', '0');
      this.hal.setAttribute('user-volume', '0');
      this.hal.setAttribute('orb-state', VoiceBotStatus.NotStarted);
      this.hal.setAttribute(
        'height',
        this.getAttribute(Attributes.height) ?? '300'
      );
      this.hal.setAttribute(
        'width',
        this.getAttribute(Attributes.width) ?? '400'
      );
      this.appendChild(this.hal);

      this.replaceIdleTimeout(30000 + '');
    }
  }
);

// Define global functions for connecting and disconnecting the agent
(globalThis as any).connectAgent = async (agent: HTMLElement) => {
  // agent.setAttribute('config', JSON.stringify(agentConfig));
  try {
    await (agent as any).connect();
    console.log('Agent connected');
  } catch (error) {
    console.error('Failed to connect:', error);
  }
};

(globalThis as any).disconnectAgent = async (agent: HTMLElement) => {
  try {
    await (agent as any).disconnect();
    console.log('Agent disconnected');
  } catch (error) {
    console.error('Failed to disconnect:', error);
  }
};
